import { queryOptions } from "@tanstack/react-query";
import type { TokenGetter } from "src/infrastructure/api/http";
import { setSessionId } from "src/infrastructure/api/sessionHeader";
import { fetchMyEntitlements, fetchPortalSession, startPortalSession } from "./session.api";
import { clearSessionRecord, readSessionRecord, saveSessionRecord } from "./session.storage";
import type { PortalSession } from "./session.types";

export const sessionQueryKeys = {
  all: ["session"] as const,
  portalSession: (userKey: string) => [...sessionQueryKeys.all, "portal", userKey] as const,
  entitlements: (userKey: string) => [...sessionQueryKeys.all, "entitlements", userKey] as const,
};

const SESSION_STALE_TIME_MS = 5 * 60 * 1000;

/** Starts a new session, never sending a previous id, and keeps its id for later calls. */
export async function restartPortalSession(
  getToken: TokenGetter,
  userKey: string
): Promise<PortalSession> {
  setSessionId(null);
  try {
    const { session, sessionId } = await startPortalSession(getToken);
    setSessionId(sessionId);
    saveSessionRecord({ userKey, sessionId });
    return session;
  } catch (error) {
    clearSessionRecord();
    throw error;
  }
}

/** Fetcher for the portal session; start policy: docs/architecture/context.md "Portal session". */
export async function loadPortalSession(
  getToken: TokenGetter,
  userKey: string
): Promise<PortalSession> {
  const record = readSessionRecord();
  if (record?.userKey !== userKey) return restartPortalSession(getToken, userKey);
  setSessionId(record.sessionId);
  return fetchPortalSession(getToken);
}

export function portalSessionQueryOptions(getToken: TokenGetter, userKey: string) {
  return queryOptions({
    queryKey: sessionQueryKeys.portalSession(userKey),
    queryFn: () => loadPortalSession(getToken, userKey),
    staleTime: SESSION_STALE_TIME_MS,
  });
}

export function entitlementsQueryOptions(getToken: TokenGetter, userKey: string) {
  return queryOptions({
    queryKey: sessionQueryKeys.entitlements(userKey),
    queryFn: () => fetchMyEntitlements(getToken),
    staleTime: SESSION_STALE_TIME_MS,
    // Every refocus, ignoring staleTime: docs/guides/app-shell.md.
    refetchOnWindowFocus: "always",
  });
}
